// src/components/vendor/VendorTypeChangeModal.tsx

import React, { useState } from 'react';
import { X, Building2, FileText, Check, ChevronDown, Upload, AlertCircle } from 'lucide-react';

interface VendorTypeChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { requested_type: string; reason: string; documents?: File[] }) => Promise<void>;
    isProcessing?: boolean;
}

const BUSINESS_TYPE_OPTIONS = [
    { value: 'sole_proprietor', label: 'Sole Proprietor', description: 'Registered sole proprietorship' },
    { value: 'llc', label: 'LLC', description: 'Limited Liability Company' },
    { value: 'corporation', label: 'Corporation', description: 'Incorporated business' },
    { value: 'partnership', label: 'Partnership', description: 'Two or more owners' },
    { value: 'non_profit', label: 'Non-Profit Organization', description: 'Charitable or non-profit entity' },
];

const DOCUMENT_REQUIREMENTS: Record<string, { key: string; label: string; required: boolean }[]> = {
    sole_proprietor: [
        { key: 'registration', label: 'Business Registration Certificate', required: true },
        { key: 'tax', label: 'Tax Registration', required: true },
        { key: 'id', label: 'Government ID', required: true },
    ],
    llc: [
        { key: 'incorporation', label: 'Articles of Incorporation', required: true },
        { key: 'registration', label: 'Business Registration Certificate', required: true },
        { key: 'tax', label: 'Tax ID / VAT Registration', required: true },
        { key: 'id', label: 'Government ID', required: true },
    ],
    corporation: [
        { key: 'incorporation', label: 'Articles of Incorporation', required: true },
        { key: 'registration', label: 'Business Registration Certificate', required: true },
        { key: 'tax', label: 'Tax ID / VAT Registration', required: true },
        { key: 'id', label: 'Government ID', required: true },
        { key: 'license', label: 'Business License', required: false },
    ],
    partnership: [
        { key: 'partnership', label: 'Partnership Agreement', required: true },
        { key: 'registration', label: 'Business Registration Certificate', required: true },
        { key: 'tax', label: 'Tax Registration', required: true },
        { key: 'id', label: 'Government ID', required: true },
    ],
    non_profit: [
        { key: 'registration', label: 'Non-Profit Registration Certificate', required: true },
        { key: 'tax', label: 'Tax Exemption Certificate', required: true },
        { key: 'id', label: 'Government ID', required: true },
    ],
};

const VendorTypeChangeModal: React.FC<VendorTypeChangeModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isProcessing = false,
}) => {
    const [requestedType, setRequestedType] = useState('');
    const [reason, setReason] = useState('');
    const [documents, setDocuments] = useState<{ key: string; file: File }[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    if (!isOpen) return null;

    const selectedType = BUSINESS_TYPE_OPTIONS.find(o => o.value === requestedType);
    const documentRequirements = requestedType ? DOCUMENT_REQUIREMENTS[requestedType] || [] : [];

    const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>, docKey: string) => {
        const file = e.target.files?.[0];
        if (file) {
            setDocuments(prev => {
                // Remove existing document with same key
                const filtered = prev.filter(d => d.key !== docKey);
                return [...filtered, { key: docKey, file }];
            });
        }
        e.target.value = '';
    };

    const removeDocument = (docKey: string) => {
        setDocuments(prev => prev.filter(d => d.key !== docKey));
    };

    const getErrorClass = (field: string) => {
        return touched[field] && errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300';
    };

    const handleSubmit = async () => {
        const newErrors: Record<string, string> = {};
        const newTouched: Record<string, boolean> = {};

        if (!requestedType) {
            newErrors.requested_type = 'Please select a business type';
            newTouched.requested_type = true;
        }
        if (!reason.trim()) {
            newErrors.reason = 'Please provide a reason for the change';
            newTouched.reason = true;
        }

        // Check required documents
        const requiredDocs = documentRequirements.filter(d => d.required);
        const missingDocs = requiredDocs.filter(doc => 
            !documents.some(d => d.key === doc.key)
        );
        if (missingDocs.length > 0) {
            newErrors.documents = `Please upload: ${missingDocs.map(d => d.label).join(', ')}`;
            newTouched.documents = true;
        }

        setErrors(newErrors);
        setTouched(prev => ({ ...prev, ...newTouched }));

        if (Object.keys(newErrors).length > 0) return;

        await onSubmit({
            requested_type: requestedType,
            reason,
            documents: documents.map(d => d.file),
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Request Business Type Change</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4">
                    <p className="text-sm text-gray-600">
                        Upgrade your account to a business type to create multiple stores. 
                        You'll need to provide supporting documents for verification.
                    </p>

                    {/* Business Type Dropdown */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Requested Business Type <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowDropdown(!showDropdown)}
                                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors ${getErrorClass('requested_type')}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                    <span className={requestedType ? 'text-gray-900' : 'text-gray-400'}>
                                        {selectedType?.label || 'Select a business type...'}
                                    </span>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showDropdown && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {BUSINESS_TYPE_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => {
                                                setRequestedType(option.value);
                                                setShowDropdown(false);
                                                setTouched(prev => ({ ...prev, requested_type: true }));
                                            }}
                                            className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                                                requestedType === option.value ? 'bg-blue-50' : ''
                                            }`}
                                        >
                                            <p className="text-sm font-medium text-gray-900">{option.label}</p>
                                            <p className="text-xs text-gray-500">{option.description}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {errors.requested_type && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" />
                                {errors.requested_type}
                            </p>
                        )}
                    </div>

                    {/* Document Upload */}
                    {requestedType && documentRequirements.length > 0 && (
                        <div className="space-y-3 pt-2 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-gray-400" />
                                <h4 className="text-sm font-medium text-gray-700">Required Documents</h4>
                                <span className="text-xs text-red-500">*</span>
                            </div>

                            <div className="space-y-2">
                                {documentRequirements.map((doc) => {
                                    const isUploaded = documents.some(d => d.key === doc.key);
                                    return (
                                        <div key={doc.key} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                                            <FileText className="h-4 w-4 text-gray-400" />
                                            <span className="text-sm text-gray-700 flex-1">
                                                {doc.label}
                                                {doc.required && <span className="text-red-500 ml-1">*</span>}
                                            </span>
                                            <label className={`cursor-pointer px-3 py-1 text-xs rounded-lg transition-colors ${
                                                isUploaded 
                                                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                            }`}>
                                                {isUploaded ? (
                                                    <span className="flex items-center gap-1">
                                                        <Check className="h-3 w-3" />
                                                        Uploaded
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1">
                                                        <Upload className="h-3 w-3" />
                                                        Upload
                                                    </span>
                                                )}
                                                <input
                                                    type="file"
                                                    accept=".pdf,.jpg,.png,.doc,.docx"
                                                    onChange={(e) => handleDocumentUpload(e, doc.key)}
                                                    className="hidden"
                                                />
                                            </label>
                                            {isUploaded && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeDocument(doc.key)}
                                                    className="text-red-400 hover:text-red-600"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                                {errors.documents && (
                                    <p className="text-sm text-red-600 flex items-center gap-1">
                                        <AlertCircle className="h-4 w-4" />
                                        {errors.documents}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Reason */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Change <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            onBlur={() => setTouched(prev => ({ ...prev, reason: true }))}
                            placeholder="Explain why you need to upgrade your business type..."
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none ${getErrorClass('reason')}`}
                        />
                        {errors.reason && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" />
                                {errors.reason}
                            </p>
                        )}
                    </div>

                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-700">
                            <strong>Note:</strong> This request will be reviewed by our team. 
                            You'll receive an update via email once it's processed.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {isProcessing ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <Check className="h-4 w-4" />
                                Submit Request
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VendorTypeChangeModal;